import { useMutation, useQuery } from '@apollo/client'
import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import { COPY_COVER_TEMPLATES } from 'data/layoutcreator/mutations/projects'
import { CHECK_FOR_CUSTOM_COVER } from 'data/layoutcreator/queries/cover'
import { GET_PROJECT_COVER_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import { GET_PROJECT_META } from 'data/layoutcreator/queries/projects'
import Head from 'shared/components/Head'
import { useLiveUsers } from 'shared/hooks/useLiveUsers'
import AdminLayout from 'shared/layout/Admin'
import { CoverSides, CoverTypes } from 'shared/types/global'
import Flex from 'shared/ui/Flex'

import LiveCursorWrapper from '../../shared/components/PageList/components/LiveCursorWrapper'
import CoverPage from '../YearbookCover/components/CoverPage'
import { CoverPageSkeleton } from '../YearbookCover/components/CoverPage/Skeleton'
import EmptyInside from '../YearbookCover/components/EmptyInside'
import Header from './components/Header'

const AdminProjectCover: React.FC = () => {
  const navigate = useNavigate()
  const liveUsers = useLiveUsers()
  const { groupId, projectId } = useParams<{
    groupId: string
    projectId: string
  }>()
  const { formatMessage } = useIntl()

  const { data: projectMetaData, loading: loadingProjectMeta } =
    useQuery(GET_PROJECT_META, {
      variables: {
        projectId: projectId || ''
      },
      skip: !projectId
    })

  const { data: coverData, loading: loadingCoverDocuments } =
    useQuery(GET_PROJECT_COVER_DOCUMENTS, {
      variables: {
        groupId: groupId || '',
        projectId: projectId || ''
      },
      skip: !groupId || !projectId || !projectMetaData
    })

  const {
    data: checkForCustomCoverData,
    loading: loadingCheckForCustomCover
  } = useQuery(CHECK_FOR_CUSTOM_COVER, {
    variables: {
      projectId: projectId || ''
    },
    skip: !projectId
  })

  const isHardCoverType =
    !loadingProjectMeta &&
    !loadingCoverDocuments &&
    projectMetaData?.getProjectMeta.coverType === CoverTypes.hard

  const [copyCoverTemplates] = useMutation(COPY_COVER_TEMPLATES)

  useEffect(() => {
    if (
      groupId &&
      projectId &&
      Array.isArray(coverData?.findProjectCoverDocuments) &&
      coverData?.findProjectCoverDocuments.length === 0
    ) {
      toast.promise(
        copyCoverTemplates({
          variables: {
            groupId: groupId,
            projectName: projectMetaData?.getProjectMeta?.name || ''
          },
          refetchQueries: [
            {
              query: GET_PROJECT_COVER_DOCUMENTS,
              variables: {
                groupId: groupId,
                projectId: projectId
              }
            }
          ]
        }),
        {
          loading: formatMessage({
            id: 'Cover.copying.loading'
          }),
          success: formatMessage({
            id: 'Cover.copying.success'
          }),
          error: formatMessage({
            id: 'Cover.copying.error'
          })
        }
      )
    }
  }, [
    coverData?.findProjectCoverDocuments,
    projectMetaData?.getProjectMeta?.name,
    groupId,
    projectId,
    copyCoverTemplates,
    formatMessage
  ])

  const handleViewSwitch = (view: 'content' | 'cover') => {
    if (view === 'content') {
      navigate(`/admin/projects/${groupId}/${projectId}/content`)
    } else {
      navigate(`/admin/projects/${groupId}/${projectId}/cover`)
    }
  }

  return (
    <AdminLayout
      showFullscreenButton={false}
      renderHeaderContent={() => (
        <Header
          liveUsers={liveUsers}
          currentView="cover"
          onViewSwitch={handleViewSwitch}
        />
      )}
    >
      <LiveCursorWrapper>
        <Head titleId="Page.editor.title" />
        <Flex
          columnGap={32}
          marginLeft={48}
          marginRight={48}
          marginBottom={48}
          marginTop={134}
        >
          {loadingProjectMeta ||
          loadingCoverDocuments ||
          loadingCheckForCustomCover ||
          (coverData?.findProjectCoverDocuments &&
            coverData.findProjectCoverDocuments.length === 0) ? (
            <>
              <CoverPageSkeleton />
              <CoverPageSkeleton />
            </>
          ) : (
            <>
              {checkForCustomCoverData?.checkForCustomCover &&
                coverData?.findProjectCoverDocuments
                  .filter(doc => {
                    return !(
                      projectMetaData?.getProjectMeta.coverType ===
                        CoverTypes.hard && doc.name.includes('inside')
                    )
                  })
                  .map(document => (
                    <CoverPage
                      document={document}
                      customCoverPreviewPath={
                        checkForCustomCoverData
                          ?.checkForCustomCover?.[
                          document.name.includes('inside')
                            ? 'inside'
                            : 'outside'
                        ]
                      }
                      key={document.id}
                      side={
                        document.name.includes('inside')
                          ? CoverSides.inside
                          : CoverSides.outside
                      }
                    />
                  ))}
              {isHardCoverType &&
              coverData?.findProjectCoverDocuments &&
              coverData?.findProjectCoverDocuments?.length > 0 ? (
                <EmptyInside />
              ) : null}
            </>
          )}
        </Flex>
      </LiveCursorWrapper>
    </AdminLayout>
  )
}

export default AdminProjectCover
